// modules/sklad.js — dátové jadro referenčného modulu Sklad & Nákup (Š1+Š2+Š3).
// VZOR pre ďalšie moduly: modul NIKDY nepridáva tabuľku — pridáva `module`
// hodnoty ('stock_item'/'stock_move') + custom_fields nad univerzálnym records
// enginom. Rezervované stĺpce kernelu (qty/unit/unit_cost_cents/container_id)
// sa mapujú top-level, VŠETKO ostatné ide do `custom` JSON stĺpca (records má
// explicitné stĺpce — nové top-level pole by INSERT zhodil, ako records.paid).
//
// Event-sourcing: stock_move je IMMUTABLE udalosť; stav skladu = reduce pohybov
// (stockLevel/stockLevels), NIKDY uložený stav → žiadny sync konflikt stavu,
// LWW rieši len jednotlivé pohyby.
//
// 0 fetch / 0 LLM / 0 nových závislostí — čistá lokálna vrstva (forever-free).
import { db, newCommonFields } from '../db';
import { isValidIsoDate, todayIso } from '../dateUtils';

// ─── Š2 · MODULE_DEF — deklaratívny descriptor modulu (DÁTA, žiadna logika) ───
// Presný tvar z FABLE-OUTPUT-JEDEN-MOZOG Š2. Builder z neho renderuje formulár,
// signals kŕmia mozog (buildSkladSignals nižšie číta from_metric → METRIC_COMPUTE).
// access.max_tier_out: 2 = skladník/kolaborant vidí kusy, NIE ceny (Tier-1) —
// tu len DEKLARÁCIA; redaction mechanika (tiers.js) príde v surface balíku.
export const MODULE_DEF = {
  module: 'sklad',
  label_sk: 'Sklad & Nákup',
  // Accent modulu — JEDINÝ zdroj pravdy pre brand farbu Sklad. Číta ho menu
  // ikona (V2Shell cez module_def.accent), PageHeader aj hero KPI dlaždice
  // (ModuleScreen), takže farba žije na JEDNOM mieste, nie na troch.
  accent: 'blue',
  entity: {
    base: 'record',
    custom_fields: [
      { stable_id: 'f_sku', label: 'Kód', type: 'text' },
      { stable_id: 'f_min', label: 'Minimum', type: 'number' },
      { stable_id: 'f_lead', label: 'Dodacia lehota (dni)', type: 'number' },
    ],
  },
  google: { sheet_tab: 'Sklad', form: 'inventura', calendar: true },
  triggers: [{ on: 'create', do: 'recomputeStockLevel' }],
  view: { filter: { module: 'stock_item' }, sort: 'label_asc' },
  access: { collaborator: true, max_tier_out: 2 },
  metrics: [
    { slug: 'hodnota_skladu', reduce: 'engine', hero: true }, // farbu preberá z MODULE_DEF.accent (ModuleScreen)
    { slug: 'pod_minimom', reduce: 'count', filter: { below_min: true }, hero: true },
    { slug: 'na_ceste', reduce: 'count', filter: { module: 'purchase_order', status: 'sent' } },
    // na_ceste_suma NIE JE v Š2 metrics zozname, ale signals ho referencujú
    // (from_metric: 'na_ceste_suma') — doplnený aby descriptor bol self-konzistentný
    // (žiadny visiaci from_metric). Suma odoslaných objednávok = viazaný cash_out.
    { slug: 'na_ceste_suma', reduce: 'sum', field: 'amount_cents', filter: { module: 'purchase_order', status: 'sent' } },
  ],
  signals: [
    { fact: 'sklad.stock_value_cents', from_metric: 'hodnota_skladu', semantic: 'asset' },
    { fact: 'sklad.items_below_min', from_metric: 'pod_minimom', semantic: 'risk' },
    { fact: 'sklad.po_committed_cents', from_metric: 'na_ceste_suma', semantic: 'cash_out' },
  ],
  surfaces: [
    {
      surface_id: 'inventura', kind: 'form_or_html', role: 'skladník',
      input: ['item_label', 'counted_qty'],
      writes: { module: 'stock_move', reason: 'inventura' },
    },
    {
      surface_id: 'objednavka', kind: 'approve_page', role: 'dodávateľ',
      payload: 'purchase_order.pdf',
      responses: ['potvrdzujem', 'termín', 'nemám'],
    },
  ],
  flows: ['flow:sklad-jit@1', 'flow:prijemka-z-dokladu@1'],
};

// Povolené smery pohybu — closed enum (privacy/consistency by construction).
export const MOVE_DIRECTIONS = ['in', 'out'];

// Celé nezáporné číslo v centoch (peniaze = int centy, nikdy float).
const isCents = (v) => Number.isInteger(v) && v >= 0;
// Konečné nezáporné číslo (množstvá/prahy — qty smie byť desatinné, napr. 2.5 kg).
const isNonNegNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

// ─── Š1 · createStockItem — katalógová karta položky (module='stock_item') ────
// input = { label (povinné), sku?, unit? (default 'ks'), min_qty?, reorder_qty?,
//           supplier_ref? (counterparty.id), lead_time_days? }
// unit → rezervovaný stĺpec kernelu; sku/min_qty/reorder_qty/supplier_ref/
// lead_time_days → custom JSON (vzor createTask/createDocumentRecord).
// Vracia uložený record. Chyby hádže (write-side gate — jeden zlý item by
// rozbil stockLevel/itemsBelowMin pre celý sklad).
export async function createStockItem(input) {
  if (typeof input?.label !== 'string' || input.label.trim() === '') {
    throw new Error('label je povinný');
  }
  for (const field of ['min_qty', 'reorder_qty', 'lead_time_days']) {
    if (input[field] != null && !isNonNegNumber(input[field])) {
      throw new Error(`${field} musí byť nezáporné číslo`);
    }
  }

  const record = {
    ...newCommonFields(),
    module: 'stock_item',
    entry_date: todayIso(), // ordering v zoznamoch (vzor createTask)
    label: input.label.trim(),
    unit: input.unit || 'ks', // rezervovaný stĺpec kernelu
    amount_cents: null,
    status: 'confirmed',
    source: 'manual',
    // Katalógové polia — JEDINÉ miesto je custom JSON (top-level by zhodilo INSERT)
    custom: {
      sku: input.sku || null,
      min_qty: input.min_qty ?? null,
      reorder_qty: input.reorder_qty ?? null,
      supplier_ref: input.supplier_ref || null,
      lead_time_days: input.lead_time_days ?? null,
    },
  };

  await db.records.add(record);
  return record;
}

// ─── Š1 · createStockMove — IMMUTABLE pohyb skladu (module='stock_move') ──────
// input = { item_ref (povinné, stock_item.id), direction ('in'|'out' povinné),
//           qty (> 0 povinné), unit? (default 'ks'), unit_cost_cents? (int centy),
//           entry_date? (default dnes), container_id? (RELATE na zákazku pri
//           výdaji → náklad zákazky), reason? }
// qty/unit/unit_cost_cents/container_id → rezervované stĺpce kernelu;
// item_ref/reason → custom JSON. Pohyb sa NIKDY neupravuje ani nemaže —
// stav skladu je vždy reduce pohybov (event-sourcing). Chyby hádže.
export async function createStockMove(input) {
  if (typeof input?.item_ref !== 'string' || input.item_ref.trim() === '') {
    throw new Error('item_ref je povinný');
  }
  if (!MOVE_DIRECTIONS.includes(input.direction)) {
    throw new Error(`direction musí byť 'in' alebo 'out'`);
  }
  const qty = input.qty;
  if (typeof qty !== 'number' || !Number.isFinite(qty) || qty <= 0) {
    throw new Error('qty musí byť číslo > 0');
  }
  // Chýbajúca cena je OK (výdaj bez ceny) — ale keď príde, musí byť int centy.
  if (input.unit_cost_cents != null && !isCents(input.unit_cost_cents)) {
    throw new Error('unit_cost_cents musí byť celé nezáporné číslo v centoch');
  }
  if (input.entry_date != null && !isValidIsoDate(input.entry_date)) {
    throw new Error('entry_date musí byť valídny ISO dátum (YYYY-MM-DD)');
  }

  const record = {
    ...newCommonFields(),
    module: 'stock_move',
    entry_date: input.entry_date || todayIso(),
    direction: input.direction, // reálny stĺpec records.direction
    qty, // rezervované stĺpce kernelu ↓
    unit: input.unit || 'ks',
    unit_cost_cents: input.unit_cost_cents ?? null,
    container_id: input.container_id || null, // RELATE na zákazku (idx_records_container_id)
    amount_cents: null,
    status: 'confirmed',
    source: input.source || 'manual',
    // item_ref/reason — JEDINÉ miesto je custom JSON (top-level by zhodilo INSERT)
    custom: {
      item_ref: input.item_ref,
      reason: input.reason || null,
    },
  };

  await db.records.add(record);
  return record;
}

// ─── Čisté reduce funkcie — stav skladu sa NIKDY neukladá ─────────────────────

// Živý pohyb skladu (nezmazaný stock_move so známym smerom). Neznámy direction
// sa ignoruje fail-safe (nefabrikuj pohyb) — createStockMove ho nepustí, toto
// chráni len pred cudzími/poškodenými dátami zo syncu.
const isLiveMove = (r) =>
  r != null &&
  r.module === 'stock_move' &&
  r.deleted_at == null &&
  MOVE_DIRECTIONS.includes(r.direction);

// Signed qty pohybu: in = +qty, out = −qty. Nečíselné qty → 0 (žiadny NaN).
const signedQty = (r) => {
  const q = Number(r.qty);
  if (!Number.isFinite(q)) return 0;
  return r.direction === 'out' ? -q : q;
};

// Balance JEDNEJ položky = Σ pohybov (in +qty, out −qty). Čistá funkcia nad
// poľom records — records aj itemRef injektnuté, žiadne DB čítanie.
export function stockLevel(records, itemRef) {
  const recs = Array.isArray(records) ? records : [];
  let level = 0;
  for (const r of recs) {
    if (!isLiveMove(r) || r.custom?.item_ref !== itemRef) continue;
    level += signedQty(r);
  }
  return level;
}

// Balance VŠETKÝCH položiek naraz (1 prechod): { item_ref → level }.
export function stockLevels(records) {
  const recs = Array.isArray(records) ? records : [];
  const levels = {};
  for (const r of recs) {
    if (!isLiveMove(r)) continue;
    const ref = r.custom?.item_ref;
    if (typeof ref !== 'string' || ref === '') continue;
    levels[ref] = (levels[ref] || 0) + signedQty(r);
  }
  return levels;
}

// Hodnota skladu v centoch = Σ signed qty × unit_cost_cents cez pohyby.
// Pohyb bez ceny prispieva 0 — advisor NIKDY nefabrikuje sumu (honest numbers):
// výdaj bez ceny hodnotu nezníži, radšej priznaná nepresnosť než vymyslená cena.
// Zaokrúhlenie RAZ per pohyb (qty smie byť desatinné, cena je int centy).
export function stockValueCents(records) {
  const recs = Array.isArray(records) ? records : [];
  let cents = 0;
  for (const r of recs) {
    if (!isLiveMove(r)) continue;
    if (!isCents(r.unit_cost_cents)) continue; // chýbajúca/zlá cena → 0 príspevok
    cents += Math.round(signedQty(r) * r.unit_cost_cents);
  }
  return cents;
}

// Položky pod minimom: živé stock_item karty s deklarovaným min_qty (custom),
// ktorých balance < min_qty (STRIKTNE — presne na minime NIE je pod minimom).
// Karta bez min_qty prah nedeklarovala → nikdy nefiruje (žiadny falošný alarm).
// Vracia [{ item_ref, label, level, min_qty }] zoradené najhlbšie pod minimom
// najprv (deterministicky, pri zhode podľa item_ref).
export function itemsBelowMin(records) {
  const recs = Array.isArray(records) ? records : [];
  const levels = stockLevels(recs);
  return recs
    .filter(
      (r) =>
        r != null &&
        r.module === 'stock_item' &&
        r.deleted_at == null &&
        isNonNegNumber(r.custom?.min_qty)
    )
    .map((r) => ({
      item_ref: String(r.id),
      label: r.label || null,
      level: levels[String(r.id)] || 0,
      min_qty: r.custom.min_qty,
    }))
    .filter((it) => it.level < it.min_qty)
    .sort(
      (a, b) => (a.level - a.min_qty) - (b.level - b.min_qty) || (a.item_ref < b.item_ref ? -1 : 1)
    );
}

// Viazaný cash_out v centoch = Σ amount_cents ODOSLANÝCH objednávok
// (module='purchase_order', status='sent', nezmazané) — peniaze na ceste von,
// vstup do cashflow forecastu mozgu.
export function poCommittedCents(records) {
  const recs = Array.isArray(records) ? records : [];
  let cents = 0;
  for (const r of recs) {
    if (r == null || r.module !== 'purchase_order' || r.deleted_at != null) continue;
    if (r.status !== 'sent') continue;
    const amt = Number(r.amount_cents);
    if (Number.isInteger(amt) && amt > 0) cents += amt;
  }
  return cents;
}

// ─── Š3 · buildSkladSignals — fakty pre mozog (advisor snapshot) ──────────────
// Deklaratívne: fakty aj semantiky žijú v MODULE_DEF.signals; tu len mapa
// from_metric → výpočet. Vracia { 'sklad.stock_value_cents': int (asset),
// 'sklad.items_below_min': int (risk), 'sklad.po_committed_cents': int (cash_out) }.
// Neznámy from_metric = chyba descriptora → throw (chyby vypíš, neprehltni).
const METRIC_COMPUTE = {
  hodnota_skladu: stockValueCents,
  pod_minimom: (records) => itemsBelowMin(records).length,
  na_ceste_suma: poCommittedCents,
};

export function buildSkladSignals(records) {
  const out = {};
  for (const sig of MODULE_DEF.signals) {
    const compute = METRIC_COMPUTE[sig.from_metric];
    if (!compute) {
      throw new Error(`sklad: signal '${sig.fact}' referencuje neznámu metriku '${sig.from_metric}'`);
    }
    out[sig.fact] = compute(records);
  }
  return out;
}
